import {Outlet, useLocation, useNavigate, useOutletContext, useSearchParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {selfcareLoginCheck, selfcareRelatedCustomers} from "../util/request";
import Menu from "../components/selfcare/Menu";
import RelatedCustomers from "../components/selfcare/RelatedCustomers";
import {Customer, CUSTOMER_STATUS} from "../util/types";
import Alert from "../components/selfcare/Alert";
import ErrorMessage from "../components/selfcare/ErrorMessage";
import LoaderSpinner from "../components/loaders/LoaderSpinner";
import Routing from "../Routing.ts";

export const CURRENT_CUSTOMER_KEY = 'current_customer'

type ContextType = { customer: Customer, setCustomer: (customer: Customer) => void }

// eslint-disable-next-line react-refresh/only-export-components
export function useCustomer() {
    return useOutletContext<ContextType>();
}

export default function SelfcareLayout() {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<boolean>(false)
    const [relatedCustomers, setRelatedCustomers] = useState<Customer[]>([])
    const [customer, setCustomer] = useState<Customer | null>(null)

    const customer_id_param = searchParams.get('customer_id') as string;

    useEffect(() => {
        if (customer_id_param !== null) {
            localStorage.setItem(CURRENT_CUSTOMER_KEY, customer_id_param)
        } else {
            localStorage.removeItem(CURRENT_CUSTOMER_KEY)
        }
    }, [customer_id_param]);

    const fetchRelatedCustomers = async () => {
            selfcareRelatedCustomers()
                .then(r => {
                    setRelatedCustomers(r.response.data)

                    if (localStorage.getItem(CURRENT_CUSTOMER_KEY)) {
                        const customerInLocalStorage = r.response.data.find((customer: Customer | undefined) => {
                            if (customer === undefined) return false
                            return customer.customer_id === localStorage.getItem(CURRENT_CUSTOMER_KEY)
                        })
                        selectCustomer(customerInLocalStorage ?? r.response.data[0])
                    } else {
                        const active_customer: Customer | undefined = r.response.data.find((c) => c.customer_context.customer_status === CUSTOMER_STATUS.ACTIVE)

                        selectCustomer(active_customer ?? r.response.data[0])
                    }

                })
                .then(() => setLoading(false))
                .catch(() => {
                    setLoading(false)
                    setError(true)
                })
        }

    useEffect(() => {
        setLoading(true)
        fetchRelatedCustomers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
         const loginCheck = async () => {
            selfcareLoginCheck().then(r => {
                const path: string = location.pathname;
                const redirect_url: string = path.includes(Routing.Selfcare.Index) && path !== Routing.Selfcare.Index ? '?redirect_url=' + path : '';
                if (!r.response.logged_in) {
                    navigate(Routing.Selfcare.Login + redirect_url)
                }
            })
        }

        loginCheck()
    }, [navigate, location.pathname]);

    const selectCustomer = (customer: Customer) => {
        setCustomer(customer)
        localStorage.setItem(CURRENT_CUSTOMER_KEY, customer.customer_id);
    }

    if (error) {
        return <ErrorMessage />
    }

    if (loading) {
        return <LoaderSpinner/>
    }

    if (relatedCustomers.length === 0 || customer === null) {
        return <SelfcarePage headerContent={<></>}>
            <span>Noget gik galt!</span>
        </SelfcarePage>
    }

    return <SelfcarePage headerContent={
        <>
            <RelatedCustomers relatedCustomers={relatedCustomers} onSelect={selectCustomer} customer={customer}/>
            <Menu customer={customer}/>
        </>
    }>
        <Outlet context={{customer, setCustomer}}/>
    </SelfcarePage>
}

function SelfcarePage(props: { headerContent: React.ReactNode, children: React.ReactNode }) {
    return <main>
        <div className="selfcare-header-wrapper">
            <div className="selfcare-header">
                {props.headerContent}
            </div>
        </div>
        <div className="selfcare-page">
            <Alert/>
            {props.children}
        </div>
    </main>
}
